import { useCallback } from 'react'
import * as Yup from 'yup'

import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { type Sx, Typography } from '@dhl/theme'
import { CARRIER } from '~/basket/model/constants'
import { isExpressOrExpressEconomy } from '~/basket/model/utils/carrier'
import { OptionInput, type OptionInputProps } from '~/basket/view/inputs/OptionInput'
import { type Price } from '~/basket/view/util/price'
import { ParcelTypeIcon } from './ParcelTypeIcon'

const classes: Record<string, Sx> = {
  boxText: {
    lineHeight: 1.6,
  },
}

export type ParcelType = {
  key: string
  carrier: string
  parcelKind:
    | 'envelope'
    | 'documents'
    | 'letterbox'
    | 'xxsmall'
    | 'xsmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'xxlarge'
    | '1kg'
    | '2kg'
    | '5kg'
    | '10kg'
    | '20kg'
  minWeightKg: number
  maxWeightKg: number
  price: Price
  dimensions: {
    maxLengthCm: number
    maxWidthCm: number
    maxHeightCm: number
    maxSumCm: number | null
  }
}
type Props = {
  name: string
  destinationCountry: string
  parcelTypes: ParcelType[]
}

export const ParcelTypeInput = ({ name, destinationCountry, parcelTypes }: Props) => {
  const { t } = useTranslation()

  const Description = useCallback(
    ({ option }: OptionInputProps<ParcelType>) => {
      const maxWeightText =
        option.maxWeightKg >= 1
          ? `${t('maximum')} ${option.maxWeightKg} kg`
          : `${option.minWeightKg * 1000} ${t('to').toLocaleLowerCase()} ${option.maxWeightKg * 1000} gr`

      if (option.dimensions.maxSumCm) {
        return <Typography sx={classes.boxText}>{t('max_sum', { maxSumCm: option.dimensions.maxSumCm })}</Typography>
      }

      if (option.carrier === CARRIER.PARCEL) {
        return (
          <>
            {option.carrier === CARRIER.PARCEL ? (
              <Typography variant="body2" sx={classes.boxText}>
                {maxWeightText}
              </Typography>
            ) : (
              <Typography variant="body2" sx={classes.boxText}>
                {t(`parcelkind_${option.parcelKind}`)}
              </Typography>
            )}
            <Typography variant="body2" sx={classes.boxText}>
              {option.dimensions.maxLengthCm} x {option.dimensions.maxWidthCm} x {option.dimensions.maxHeightCm} cm
            </Typography>
          </>
        )
      }

      if (isExpressOrExpressEconomy(option.carrier)) {
        return (
          <>
            {option.parcelKind === 'documents' ? (
              <Typography variant="body2" sx={classes.boxText}>
                {maxWeightText}
              </Typography>
            ) : (
              <Typography variant="body2" sx={classes.boxText}>
                {t('max', {
                  maxLengthCm: option.dimensions.maxLengthCm,
                  maxWidthCm: option.dimensions.maxWidthCm,
                  maxHeightCm: option.dimensions.maxHeightCm,
                  maxKg: option.maxWeightKg,
                })}
              </Typography>
            )}
            <Typography variant="body2" sx={classes.boxText}>
              {t(`parcelkind_${option.parcelKind}`)}
            </Typography>
          </>
        )
      }

      return (
        <Typography variant="body2" sx={classes.boxText}>
          {t(`parcelkind_${option.parcelKind}`)}
        </Typography>
      )
    },
    [t]
  )

  const Title = useCallback(
    ({ option }: OptionInputProps<ParcelType>) => {
      if (option.dimensions.maxSumCm) {
        return null
      }
      if (option.carrier === CARRIER.PARCEL) {
        return (
          <Typography noWrap variant="h5">
            {t(`parcelkind_${option.parcelKind}`)}
          </Typography>
        )
      }
      if (isExpressOrExpressEconomy(option.carrier)) {
        return (
          <Typography noWrap variant="h5">
            {t(`parcelkind_express_${option.parcelKind}`)}
          </Typography>
        )
      }

      return (
        <Typography variant="h5">
          {t('maximum')} {option.maxWeightKg} kg
        </Typography>
      )
    },
    [t]
  )

  const parcelTypesSortedByPrice = [...parcelTypes].sort((a, b) => a.price.withTax - b.price.withTax)

  return (
    <OptionInput
      dataTestPrefix={destinationCountry}
      name={name}
      options={parcelTypesSortedByPrice}
      icon={ParcelTypeIcon}
      title={Title}
      description={Description}
    />
  )
}

export const createParcelTypeValidationSchema = (t: TFunction) =>
  Yup.string().nullable().required(t('errorKeys.errorParcelTypeRequired'))
